// Isolation checks (P0.9) — Architecture §11: "/platform — capability detection, audio devices,
// isolation checks, storage adapters".
//
// Everything from P0.3 onward rests on SharedArrayBuffer, and SharedArrayBuffer exists only when
// the page is cross-origin isolated. That is not a browser capability the user can install their
// way out of — it is two response headers. So when it is missing, the honest thing is to say
// which layer failed, because the fix lives in a different place for each.
//
// FR-42's posture, in miniature: "unsupported browsers and mobile devices get a friendly
// explanation page, never a broken app". The full gate (mobile detection, browser tiering,
// AudioWorklet and WebGL2 probes) is the app shell's, later. This is the part the engine cannot
// start without.

/** Why isolation is unavailable. Each points at a different fix. */
export type IsolationFailure =
  /** Not https (and not localhost). SAB is a secure-context feature; nothing else can help. */
  | 'insecure-context'
  /** The browser predates the API entirely — pre-2020, roughly. */
  | 'unsupported-browser'
  /** Secure and modern, but the headers are absent. A hosting problem, and ours to fix. */
  | 'headers-missing'
  /** Headers present, but this document is framed cross-origin. OQ-A1 says that is unsupported. */
  | 'embedded-cross-origin';

export interface IsolationStatus {
  /** True when the engine can start. Everything else here explains a false. */
  readonly ok: boolean;
  readonly crossOriginIsolated: boolean;
  readonly sharedArrayBuffer: boolean;
  readonly secureContext: boolean;
  readonly failure: IsolationFailure | null;
}

/** `window.top === window.self` throws when framed cross-origin — which is itself the answer. */
function isFramed(): boolean {
  try {
    return globalThis.self !== globalThis.top;
  } catch {
    return true;
  }
}

/**
 * Checks whether the engine can exist, and if not, why.
 *
 * Ordered from "no fix exists" to "our deployment is wrong", so the first true cause is the one
 * reported. A page served over http *and* unframed *and* header-less should say
 * `insecure-context`, because fixing the headers would not help it.
 */
export function checkIsolation(): IsolationStatus {
  const secureContext = globalThis.isSecureContext;
  const sharedArrayBuffer = typeof SharedArrayBuffer !== 'undefined';
  const crossOriginIsolated = globalThis.crossOriginIsolated;

  if (crossOriginIsolated && sharedArrayBuffer) {
    return {
      ok: true,
      crossOriginIsolated,
      sharedArrayBuffer,
      secureContext,
      failure: null,
    };
  }

  const failure: IsolationFailure = !secureContext
    ? 'insecure-context'
    : typeof globalThis.crossOriginIsolated === 'undefined'
      ? 'unsupported-browser'
      : isFramed()
        ? 'embedded-cross-origin'
        : 'headers-missing';

  return { ok: false, crossOriginIsolated, sharedArrayBuffer, secureContext, failure };
}
